"use client";

import { useBreakpoint } from "@dc/hooks";
import { Button } from "@dc/ui/components";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTranslations } from "next-intl";
import type { FC } from "react";
import type { ErrorPageContentProps } from "./ErrorPageContent.types";

// APP_ERROR is a client-side JS exception, not an HTTP failure: it shares the 500 copy
// but gets a neutral illustration so we don't claim the server failed.
const PAGE_COPY: Record<string, "unauthorized" | "forbidden" | "not_found" | "server_error" | "service_unavailable"> = {
  "401": "unauthorized",
  "403": "forbidden",
  "404": "not_found",
  "500": "server_error",
  "503": "service_unavailable",
  APP_ERROR: "server_error",
};

const ConstructionIllustration401 = dynamic(
  () => import("@dc/ui/illustrations").then((mod) => mod.ConstructionIllustration401),
  { ssr: false },
);
const ConstructionIllustration403 = dynamic(
  () => import("@dc/ui/illustrations").then((mod) => mod.ConstructionIllustration403),
  { ssr: false },
);
const ConstructionIllustration404 = dynamic(
  () => import("@dc/ui/illustrations").then((mod) => mod.ConstructionIllustration404),
  { ssr: false },
);
const ConstructionIllustration500 = dynamic(
  () => import("@dc/ui/illustrations").then((mod) => mod.ConstructionIllustration500),
  { ssr: false },
);
const ConstructionIllustration503 = dynamic(
  () => import("@dc/ui/illustrations").then((mod) => mod.ConstructionIllustration503),
  { ssr: false },
);
const BuilderWithPipe = dynamic(() => import("@dc/ui/illustrations").then((mod) => mod.BuilderWithPipe), {
  ssr: false,
});

export const ErrorPageContent: FC<ErrorPageContentProps> = ({ errorCode, title, description, action }) => {
  const t = useTranslations("errors.page");
  const copy = PAGE_COPY[errorCode] ?? "server_error";
  const resolvedTitle = title ?? t(`${copy}.title`);
  const resolvedDescription = description ?? t(`${copy}.description`);

  const isMobile = !useBreakpoint("sm");
  const renderButton = () => {
    if (!action) return null;

    if ("node" in action) {
      return action.node;
    }

    const buttonContent = (
      <>
        {action.icon}
        {action.label}
      </>
    );

    if ("href" in action) {
      return (
        <Button
          as={Link}
          href={action.href}
          variant="bordered"
          color="primary"
          className="gap-2"
          size={isMobile ? "lg" : "md"}
        >
          {buttonContent}
        </Button>
      );
    }

    return (
      <Button
        variant="bordered"
        color="primary"
        className="gap-2"
        onPress={action.onClick}
        size={isMobile ? "lg" : "md"}
        isDisabled={action.isLoading}
        isLoading={action.isLoading}
      >
        {buttonContent}
      </Button>
    );
  };

  const renderIllustration = () => {
    const illustrationClass = "h-auto w-[185px] text-foreground";
    switch (errorCode) {
      case "401":
        return <ConstructionIllustration401 className={illustrationClass} />;
      case "403":
        return <ConstructionIllustration403 className={illustrationClass} />;
      case "404":
        return <ConstructionIllustration404 className={illustrationClass} />;
      case "503":
        return <ConstructionIllustration503 className={illustrationClass} />;
      case "APP_ERROR":
        return <BuilderWithPipe className={illustrationClass} />;
      default:
        return <ConstructionIllustration500 className={illustrationClass} />;
    }
  };

  return (
    <div
      data-testid="error-page"
      className="flex min-h-[400px] w-full flex-col items-center justify-center px-6 py-12 md:flex-row md:gap-6"
    >
      <div className="mb-8 flex shrink-0 justify-center md:mb-0">{renderIllustration()}</div>
      <div className="flex max-w-[543px] flex-col items-center text-center md:items-start md:text-left">
        <h1 className="mb-2 font-semibold text-2xl text-foreground">{resolvedTitle}</h1>
        <p className="mb-6 text-base text-foreground">{resolvedDescription}</p>
        {renderButton()}
      </div>
    </div>
  );
};
